import React, { useState } from "react";
import { toast } from "sonner";
import { FileDown, CheckCircle2, Loader2, Undo2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadBlob } from "@/components/hr/hrUtils";

const PAY = ["Virement", "Chèque", "Espèces", "Prélèvement", "Carte bancaire"];

export function RetroActions({ r, year, month, onChanged }) {
  const pid = r.practitioner.id;
  const toPrat = r.solde >= 0;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState("");
  const pdf = async () => {
    setBusy("pdf");
    const slug = r.practitioner.name.replace(/[^A-Za-z0-9]+/g, "-");
    try { await downloadBlob(`/retrocessions/${pid}/statement.pdf?year=${year}&month=${month}`, `releve-retrocession_${slug}_${year}-${String(month).padStart(2, "0")}.pdf`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const start = () => { setF(r.payment ? { date: r.payment.date, payment_method: r.payment.payment_method, amount: String(r.payment.amount) } : { date: new Date().toISOString().slice(0, 10), payment_method: "Virement", amount: Math.abs(r.solde).toFixed(2) }); setOpen(true); };
  const save = async () => {
    setBusy("pay");
    try { await api.post(`/retrocessions/${pid}/payment`, { year, month, ...f }); toast.success(toPrat ? "Règlement enregistré dans Dépenses" : "Reversement enregistré dans les recettes"); setOpen(false); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const cancel = async () => {
    if (!window.confirm("Annuler ce règlement ? La dépense liée sera supprimée.")) return;
    try { await api.delete(`/retrocessions/${pid}/payment?year=${year}&month=${month}`); toast.success("Règlement annulé"); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const p = r.payment;
  const diff = p && Math.abs(p.amount - Math.abs(r.solde)) > 0.009;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`retro-actions-${pid}`}>
      <Button data-testid={`retro-pdf-${pid}`} variant="outline" size="sm" disabled={busy === "pdf"} onClick={pdf} className="rounded-full">
        {busy === "pdf" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />}Relevé PDF à signer
      </Button>
      {p ? (
        <>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-[#065F46]" data-testid={`retro-paid-${pid}`}>
            <CheckCircle2 className="h-3.5 w-3.5" />Réglé le {fmtDate(p.date)} · {fmtEUR2(p.amount)} · {p.payment_method}
          </span>
          {diff && <span className="text-xs font-semibold text-amber-700" data-testid={`retro-paid-diff-${pid}`}>Le solde a changé depuis ({fmtEUR2(Math.abs(r.solde))})</span>}
          <Button data-testid={`retro-pay-edit-${pid}`} variant="ghost" size="sm" onClick={start} className="rounded-full">Modifier</Button>
          <Button data-testid={`retro-pay-cancel-${pid}`} variant="ghost" size="sm" onClick={cancel} className="rounded-full text-rose-600 hover:bg-rose-50"><Undo2 className="mr-1 h-4 w-4" />Annuler</Button>
        </>
      ) : Math.abs(r.solde) >= 0.005 && (
        <Button data-testid={`retro-pay-${pid}`} size="sm" onClick={start} className="rounded-full bg-[#065F46] hover:bg-[#047857]">
          <CheckCircle2 className="mr-1 h-4 w-4" />{toPrat ? "Marquer comme versé" : "Marquer le reversement reçu"}
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid={`retro-pay-dialog-${pid}`}>
          <DialogHeader><DialogTitle className="font-display text-xl">{toPrat ? `Versement à ${r.practitioner.name}` : `Reversement de ${r.practitioner.name}`}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500">{toPrat ? "Enregistré comme dépense payée dans Dépenses." : "Enregistré comme recette encaissée."}</p>
          <div className="space-y-3">
            <div><Label className="text-xs text-gray-500">Date du règlement</Label><Input data-testid="retro-pay-date" type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs text-gray-500">Montant (€)</Label><Input data-testid="retro-pay-amount" type="number" step="0.01" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs text-gray-500">Mode de règlement</Label>
              <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
                <SelectTrigger data-testid="retro-pay-method" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button data-testid="retro-pay-submit" disabled={busy === "pay"} onClick={save} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">
              {busy === "pay" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enregistrer le règlement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
